import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Observable,
  throwError,
} from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Empresa, EmpresaInit } from '../../_models/empresa.model';
import { SnackBarService } from '../../components/snack-bar/snackBar.service';
import { GtmService } from '../gtm/gtm.service';
import { EmpresaScmInfo } from '../../_views/create-supplier/create-supplier.model';
@Injectable({
  providedIn: 'root',
})
export class EmpresaService {
  private apiUrl = `${environment.urlApi}/empresas`;

  constructor(
    private http: HttpClient,
    private snackBarService: SnackBarService,
    private gtmService: GtmService
  ) {}

  getEmpresaById(id: string): Observable<Empresa> {
    return this.http
      .get<Empresa>(`${this.apiUrl}/${id}`)
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.error('Error fetching empresa by id:', error);
          return throwError(() => new Error('Error fetching empresa'));
        })
      );
  }

   /**
    * Actualiza parcialmente la información de una empresa usando PATCH
    * Solo envía los campos modificados
    * @param id ID de la empresa
    * @param changes Objeto con los campos modificados
    * @returns Observable con la empresa actualizada
    */
   updateEmpresaPartial(id: string, changes: Record<string, any>): Observable<Empresa> {
     return this.http
       .patch<Empresa>(`${this.apiUrl}/${id}`, changes)
       .pipe(
         tap((updatedEmpresa) => {
           // Mostrar mensaje de éxito
           this.snackBarService.displaySnackBar(
             'success',
             'Éxito',
             'Empresa actualizada exitosamente'
           );

           // Evento GTM
           this.gtmService.push({
             dominio: 'empresa',
             accion: 'actualizar_parcial',
             componente_ui: 'empresa_service',
             valor: id,
             resultado_accion: 'exitoso'
           });
         }),
         catchError((error) => {
           console.error('Error updating empresa partially:', error);
           return throwError(() => new Error('Error updating empresa'));
         })
       );
   }

  /**
   * Actualiza la información de una empresa
   * @param id ID de la empresa
   * @param empresa Objeto completo de la empresa actualizada
   * @returns Observable con la empresa actualizada
   */
  updateEmpresa(id: string, empresa: Empresa): Observable<Empresa> {
    return this.http
      .put<Empresa>(`${this.apiUrl}/${id}`, empresa)
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.error('Error updating empresa:', error);
          return throwError(() => new Error('Error updating empresa'));
        })
      );
  }

  /**
   * Obtiene la información de empresa desde el WS get-info-empresa-scm
   * @param rut RUT de la empresa (formato: 12345678-9)
   * @returns Observable con la información de la empresa
   */
  getInfoEmpresaScm(rut: string): Observable<EmpresaScmInfo> {
    return this.http
      .get<EmpresaScmInfo>(`${this.apiUrl}/get-info-empresa-scm/${rut}`)
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.log('No se encontró información de empresa SCM para RUT:', rut);
          return throwError(() => new Error('NOT_FOUND'));
        })
      );
  }

  /**
   * Obtiene la información de empresa y sus roles
   * @param companyId  empresa
   * @param orgId  centro gestion
   * @param user  userName
   * @returns Observable con la información de rut, id y roles
   */
  initialize(companyId: string, orgId: string, user: string): Observable<EmpresaInit> {
    return this.http
      .get<EmpresaInit>(`${this.apiUrl}/initialize/${companyId}/${orgId}/${user}`)
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.log('No se encontró información de empresa para inicializar:', companyId);
          return throwError(() => new Error('NOT_FOUND'));
        })
      );
  }
}
